import type { Ref } from "react";
import { Trans } from "react-i18next";
import { Checkbox } from "./checkbox";
import { Icon } from "./icon";
import type {
  Option,
  OptionRendererProps,
  SearchableSelectProps,
} from "./searchable-select";
import { SearchableSelect } from "./searchable-select";
import { TruncatedText } from "./truncated-text";

export interface BadgeSearchableSelectProps<T>
  extends Omit<SearchableSelectProps<T>, "optionRenderer"> {
  sections?: Array<{
    title: string;
    options: Option<T>[];
  }>;
  options?: Option<T>[];
}

const BadgeOptionRenderer = <T,>({
  option,
  isSelected,
  isMulti,
  onSelect,
}: OptionRendererProps<T>) => {
  const badgeOption = option;

  return (
    <div
      className="flex items-center justify-between w-full"
      style={{
        maxWidth: "calc(var(--radix-popover-trigger-width) - 16px)",
      }}
    >
      {isMulti ? (
        <div className="flex items-center">
          <Checkbox
            checked={isSelected}
            onCheckedChange={onSelect}
            className="mr-2"
          />
          <TruncatedText>{badgeOption.label}</TruncatedText>
        </div>
      ) : (
        <div className="flex w-full items-center justify-between gap-2">
          <TruncatedText>{badgeOption.label}</TruncatedText>
          {badgeOption.badge && (
            <span className="flex-shrink-0 rounded-sm min-w-4 h-4 font-normal whitespace-nowrap text-gray-700">
              <Trans i18nKey={badgeOption.badge} />
            </span>
          )}
          {isSelected && <Icon icon="check" className="text-primary" />}
        </div>
      )}
    </div>
  );
};

export function BadgeSearchableSelect<T>(
  props: BadgeSearchableSelectProps<T> & { ref?: Ref<HTMLButtonElement> },
) {
  const { ref, ...rest } = props;
  return (
    <SearchableSelect
      {...rest}
      ref={ref}
      optionRenderer={BadgeOptionRenderer}
    />
  );
}
